import clsx from 'clsx'
import { html, nothing, PropertyValueMap, unsafeCSS } from 'lit'
import { property, query } from 'lit/decorators.js'

import { DdsElement } from '@/internal/dds-hu-element'
import { translate } from '@/localization/localization'

import { ButtonVariant } from '../button/button.component'
import styles from './modal.scss?inline'

type CloseSource =
  | 'ok-button'
  | 'cancel-button'
  | 'close-button'
  | 'esc'
  | 'overlay'

export default class ModalBaseElement extends DdsElement {
  @query('.modal') dialog!: HTMLDialogElement
  @query('.modal__box') modalBox!: HTMLElement
  @property({ type: Boolean, reflect: true }) open?: boolean = false
  @property({ type: Boolean, reflect: true }) fullScreen?: boolean = false
  @property({ type: String, reflect: true })
  closeOnEsc = 'true'
  @property({
    type: String,
    reflect: true,
  })
  closeOnOverlayClick = 'true'
  @property({ type: String, reflect: true }) closeButton = 'true'
  @property() title: string
  @property() description: string
  @property() okButtonLabel: string
  @property() cancelButtonLabel: string
  @property() closeButtonLabel: string
  @property() okButtonVariant: ButtonVariant = 'primary'
  @property() cancelButtonVariant: ButtonVariant = 'outline'
  @property({ type: Boolean }) okButtonDisabled = false
  @property({ type: Boolean }) cancelButtonDisabled = false
  @property({ type: Boolean }) cancelButtonDanger = false
  @property({ type: Boolean }) okButtonDanger = false
  @property({ type: Boolean }) hideOkButton = false
  @property({ type: Boolean }) hideCancelButton = false
  @property({ type: String }) footer = 'true'
  @property({ type: String }) header = 'true'
  @property({ type: Boolean }) wideFooter = false
  @property({ type: Boolean }) bottomFooter = false
  @property({ type: Boolean }) useNativeFocus = false
  private previouslyFocusedElement: Element | null = null
  private focusableElements: HTMLElement[] = []

  private canceled = false

  static readonly styles = unsafeCSS(styles)

  attributeChangedCallback(
    name: string,
    _old: string | null,
    value: string | null,
  ): void {
    if (name === 'title') {
      this.header = value ? 'true' : 'false'
    }
    super.attributeChangedCallback(name, _old, value)
  }

  protected updated(
    _changedProperties: PropertyValueMap<any> | Map<PropertyKey, unknown>,
  ): void {
    super.updated(_changedProperties)
    if (
      _changedProperties.has('open') &&
      _changedProperties.get('open') !== undefined
    ) {
      if (this.open) {
        this.handleOpen()
      } else {
        this.handleCloseAction()
      }
    }
  }

  private handleOpen(): void {
    this.emit('dds-before-open')
    this.scrollLock(true)
    this.previouslyFocusedElement = this.getActiveElement()
    this.dialog.showModal()
    this.dialog.addEventListener(
      'transitionend',
      () => {
        this.emit('dds-opened')
        this.focusableElements = this.getFocusableElements()
        if (this.focusableElements.length > 0) {
          this.focusableElements[0].focus()
        }
      },
      { once: true },
    )
    this.setAttribute('aria-hidden', 'false')
  }

  private handleCloseAction(): void {
    this.emit('dds-before-close')
    this.dialog.close()
    this.scrollLock(false)
    this.dialog.addEventListener(
      'transitionend',
      () => {
        this.emit('dds-closed')
        if (this.previouslyFocusedElement instanceof HTMLElement) {
          this.previouslyFocusedElement.focus()
        }
      },
      { once: true },
    )
    this.setAttribute('aria-hidden', 'true')
  }

  scrollLock = (open: boolean) => {
    if (open) {
      document.body.style.overflow = 'hidden'
      document.body.dataset.modalOpen = 'true'
    } else {
      document.body.style.overflow = ''
      delete document.body.dataset.modalOpen
    }
  }

  protected firstUpdated(
    _changedProperties: PropertyValueMap<any> | Map<PropertyKey, unknown>,
  ): void {
    super.firstUpdated(_changedProperties)
    if (this.open) {
      this.setAttribute('aria-hidden', 'false')

      this.dialog.showModal()
    } else {
      this.setAttribute('aria-hidden', 'true')
    }
  }

  public show(): void {
    this.open = true
  }

  public hide(): void {
    this.open = false
  }

  private handleClose(source: CloseSource) {
    const handleCloseEvent = this.emit(
      'dds-close',
      {
        source,
      },
      {
        cancelable: true,
      },
    )

    if (handleCloseEvent.defaultPrevented) {
      return
    }

    this.hide()
  }

  private handleCancel(event: Event): void {
    if (this.closeOnEsc !== 'true') {
      event.preventDefault()
      this.canceled = true
    }
  }

  private handleEscClose(event: Event): void {
    if (this.canceled) {
      event.preventDefault()
      return
    }
    if (this.open) {
      this.handleClose('esc')
    }
  }

  private handleOkButton(): void {
    this.emit('dds-ok')
    this.handleClose('ok-button')
  }

  private handleCancelButton(): void {
    this.emit('dds-cancel')
    this.handleClose('cancel-button')
  }

  private handleClick(event: MouseEvent): void {
    if (this.closeOnOverlayClick === 'true' && event.target === this.dialog) {
      this.handleClose('overlay')
    }
  }

  private handleKeDown(event: KeyboardEvent): void {
    if (event.key === 'Tab' && !this.useNativeFocus) {
      this.handleFocusTrap(event)
      return
    }

    if (this.closeOnEsc === 'true' && event.key === 'Escape') {
      this.handleClose('esc')
    } else if (event.key === 'Escape') {
      event.preventDefault()
      event.stopImmediatePropagation()
    }
  }

  private getFocusableElements(): HTMLElement[] {
    const focusableSelectors = [
      'button',
      '[href]',
      'input',
      'select',
      'textarea',
      '[tabindex]:not([tabindex="-1"])',
      '[role="button"]',
      '[role="link"]',
      '[role="menuitem"]',
      '[role="tab"]',
      '[role="option"]',
      '[role="radio"]',
      '[role="checkbox"]',
      '[role="switch"]',
      '[role="combobox"]',
      '[role="listbox"]',
      '[role="textbox"]',
      '[contenteditable="true"]',
      'dap-ds-input',
      'dap-ds-textarea',
      'dap-ds-select',
      'dap-ds-button',
      'dap-ds-icon-button',
      'dap-ds-search-input',
      'dap-ds-combobox',
      'dap-ds-checkbox',
      'dap-ds-radio',
      'dap-ds-switch',
      'dap-ds-slider',
      'dap-ds-file-input',
      'dap-ds-calendar',
      'dap-ds-date-picker',
      'slot',
    ]

    const getAllFocusableElements = (
      root: Element | ShadowRoot,
    ): HTMLElement[] => {
      const elements: HTMLElement[] = []

      // Get all elements matching the selectors in the current root
      const matches = root.querySelectorAll(focusableSelectors.join(','))
      matches.forEach(element => {
        if (element instanceof HTMLSlotElement) {
          const assignedElements = element.assignedElements()
          assignedElements.forEach(assignedElement => {
            if (assignedElement instanceof HTMLElement) {
              elements.push(assignedElement)
            }
          })
        } else if (element instanceof HTMLElement) {
          elements.push(element)
        }
      })

      return elements
    }

    return getAllFocusableElements(this.modalBox)
  }

  private getActiveElement(): Element | null {
    const activeElement = document.activeElement
    if (!activeElement) return null

    // If the active element is a web component, try to get the focused element inside its shadow root
    if (activeElement.shadowRoot) {
      const focusedElement = activeElement.shadowRoot.activeElement
      if (focusedElement) {
        return focusedElement
      }
    }

    return activeElement
  }

  private getElementFromElement(element: Element): HTMLElement {
    if (element.shadowRoot) {
      return element.shadowRoot.activeElement as HTMLElement
    }
    return element as HTMLElement
  }

  private handleFocusTrap(event: KeyboardEvent): void {
    if (event.key !== 'Tab' || this.useNativeFocus) return

    if (this.focusableElements.length === 0) {
      this.focusableElements = this.getFocusableElements()
    }

    const firstFocusableElement = this.focusableElements[0]
    const lastFocusableElement = this.getElementFromElement(
      this.focusableElements[this.focusableElements.length - 1],
    )

    const activeElement = this.getActiveElement()

    if (!activeElement) return

    if (event.shiftKey) {
      if (activeElement === firstFocusableElement) {
        event.preventDefault()
        lastFocusableElement.focus()
      }
    } else {
      if (activeElement === lastFocusableElement) {
        event.preventDefault()
        firstFocusableElement.focus()
      }
    }
  }

  renderModal() {
    return html`<dialog
      part="base"
      class=${clsx('modal', {
        'modal--open': this.open,
        'modal--full': this.fullScreen,
      })}
      aria-label=${`${this.title ?? ''} ${this.description ?? ''}`}
      role="dialog"
      @close=${this.handleEscClose}
      @cancel=${this.handleCancel}
      @click=${this.handleClick}
      @keydown=${this.handleKeDown}>
      <div
        part="panel"
        class="modal__box"
        aria-modal="true"
        aria-hidden=${this.open ? 'false' : 'true'}>
        <div class="modal__header-container" part="header-container">
          ${this.header === 'true'
            ? html`<header class="modal__header" part="header" id="header">
                <slot name="title">
                  <dap-ds-typography variant="h3" part="title"
                    >${this.title}</dap-ds-typography
                  >
                </slot>
                <slot name="description">
                  <span
                    class="modal__description"
                    part="description"
                    id="description">
                    ${this.description}
                  </span>
                </slot>
              </header>`
            : html`<span></span>`}
          ${this.closeButton === 'true'
            ? html`<dap-ds-icon-button
                part="closebutton"
                class="modal__close"
                aria-label=${this.closeButtonLabel
                  ? this.closeButtonLabel
                  : translate('close')}
                @click=${() => this.handleClose('close-button')}>
                <dap-ds-icon name="close-line"></dap-ds-icon>
              </dap-ds-icon-button>`
            : nothing}
        </div>
        <div
          class=${clsx('modal__content', {
            'modal__content--bottom': this.bottomFooter,
          })}
          part="content">
          <slot></slot>
        </div>
        ${this.footer === 'true'
          ? html`<footer
              class=${clsx('modal__footer', {
                'modal__footer--wide': this.wideFooter,
              })}
              part="footer">
              <slot name="footer">
                ${this.hideOkButton
                  ? nothing
                  : html`<dap-ds-button
                      @click=${this.handleOkButton}
                      variant=${this.okButtonVariant}
                      ?danger=${this.okButtonDanger}
                      ?disabled=${this.okButtonDisabled}
                      ?hidden=${this.hideOkButton}
                      >${this.okButtonLabel ?? translate('ok')}</dap-ds-button
                    >`}
                ${this.hideCancelButton
                  ? nothing
                  : html`<dap-ds-button
                      variant=${this.cancelButtonVariant}
                      ?danger=${this.cancelButtonDanger}
                      ?disabled=${this.cancelButtonDisabled}
                      ?hidden=${this.hideCancelButton}
                      @click=${this.handleCancelButton}
                      >${this.cancelButtonLabel ??
                      translate('cancel')}</dap-ds-button
                    >`}
              </slot>
            </footer>`
          : nothing}
      </div>
    </dialog>`
  }
}
